import React from 'react';
import './NextModeWorkspace.css';

export const NEXT_MODES = [
  {
    id: 'idea', label: 'Explore', icon: '✦', color: '#8b7cff',
    job: 'Find the opportunity others miss', deliverable: 'Opportunity model',
    description: 'Map users, tensions, mechanisms, assumptions, and the cheapest tests that could change your mind.',
    placeholder: 'What opportunity, product, or problem should we explore?',
    examples: ['A defensible wedge for group travel', 'A better way for teams to remember decisions', 'What could replace weekly status meetings?'],
    stages: ['Frame', 'Explore', 'Challenge', 'Test'], primary: 'Challenge the model', actionId: 'critique',
  },
  {
    id: 'decision', label: 'Decide', icon: '⌘', color: '#ffb86b',
    job: 'Make a consequential choice with clarity', deliverable: 'Decision brief',
    description: 'Compare real options, expose trade-offs, weigh evidence, and define the conditions that would reverse the choice.',
    placeholder: 'What decision are you making, by when, and what makes it hard?',
    examples: ['Should we expand into Europe this year?', 'Hire a sales leader or stay founder-led?', 'Build, buy, or partner for analytics?'],
    stages: ['Frame', 'Options', 'Evidence', 'Commit'], primary: 'Stress-test choice', actionId: 'critique',
  },
  {
    id: 'writing', label: 'Write', icon: '¶', color: '#d99cff',
    job: 'Turn thinking into a persuasive argument', deliverable: 'Structured draft',
    description: 'Clarify audience, thesis, evidence, narrative order, objections, and the draft that follows from them.',
    placeholder: 'What are you writing, for whom, and what should change after they read it?',
    examples: ['A board memo proposing new pricing', 'An essay on AI-native organizations', 'A launch narrative for our beta'],
    stages: ['Intent', 'Argument', 'Editorial', 'Draft'], primary: 'Shape the draft', actionId: 'refine',
  },
  {
    id: 'plan', label: 'Plan', icon: '▰', color: '#57d7a3',
    job: 'Turn an outcome into executable work', deliverable: 'Execution plan',
    description: 'Build milestones, dependencies, owners, risks, decision gates, and a credible first move.',
    placeholder: 'What outcome must happen, by when, and under which constraints?',
    examples: ['Launch our beta in six weeks', 'Move the team to a new pricing model', 'Plan the first 90 days of expansion'],
    stages: ['Outcome', 'Sequence', 'Risk', 'Launch'], primary: 'Stress-test plan', actionId: 'critique',
  },
  {
    id: 'learn', label: 'Learn', icon: '◈', color: '#63c7ff',
    job: 'Build durable understanding—not a summary', deliverable: 'Mastery map',
    description: 'Organize prerequisites, concepts, explanations, misconceptions, and active probes of what you understand.',
    placeholder: 'What do you want to understand, and what can you already explain?',
    examples: ['Causal inference from first principles', 'How transformers actually learn', 'The economics of marketplace liquidity'],
    stages: ['Baseline', 'Concepts', 'Probe', 'Master'], primary: 'Test understanding', actionId: 'learn',
  },
  {
    id: 'codebase', label: 'Code', icon: '⟨/⟩', color: '#7bdcf4',
    job: 'Understand a system before changing it', deliverable: 'System map',
    description: 'Trace architecture, boundaries, flows, risks, and concrete changes back to the code that supports them.',
    placeholder: 'Choose a repository or upload source files to begin.',
    examples: ['Find risky service boundaries', 'Explain the generation data flow', 'Plan a safe authentication migration'],
    stages: ['Ingest', 'Map', 'Audit', 'Change'], primary: 'Audit the system', actionId: 'critique',
  },
  {
    id: 'resume', label: 'Career', icon: '◎', color: '#74c0fc',
    job: 'Translate your experience into a credible case', deliverable: 'Application strategy',
    description: 'Connect evidence from your work to the role, identify gaps, and shape a precise application narrative.',
    placeholder: 'Add your resume and the role you want to pursue.',
    examples: ['Product leader at an AI startup', 'Staff engineer for platform infrastructure', 'Strategy role in climate technology'],
    stages: ['Role', 'Evidence', 'Gaps', 'Position'], primary: 'Review the strategy', actionId: 'critique',
  },
];

export function getNextMode(mode) {
  return NEXT_MODES.find(item => item.id === mode) || NEXT_MODES[0];
}

export function NextModeHeader({ mode, nodeCount, isGenerating, hasMemory, onSelectMode, onPrimaryAction, actionDisabled }) {
  const current = getNextMode(mode);
  return (
    <section className="nxw-header" style={{ '--nxw-accent': current.color }}>
      <div className="nxw-mode-strip" role="tablist" aria-label="Mission mode">
        {NEXT_MODES.map(item => (
          <button
            key={item.id}
            role="tab"
            aria-selected={item.id === current.id}
            className={item.id === current.id ? 'is-active' : ''}
            onClick={() => onSelectMode(item.id)}
            title={`${item.job} · produces ${item.deliverable}`}
          >
            <span>{item.icon}</span>{item.label}
          </button>
        ))}
      </div>
      <div className="nxw-identity">
        <div className="nxw-identity-mark">{current.icon}</div>
        <div className="nxw-identity-copy">
          <span>{current.label} · {current.deliverable}</span>
          <strong>{current.job}</strong>
          <p>{current.description}</p>
        </div>
        <div className="nxw-context">
          {hasMemory && <span className="is-memory">◇ Claude memory attached</span>}
          {nodeCount > 0 && <span>{nodeCount} connected findings</span>}
          {nodeCount > 0 && !isGenerating && (
            <button onClick={() => onPrimaryAction(current.actionId)} disabled={actionDisabled}>{current.primary} <b>→</b></button>
          )}
        </div>
      </div>
      <div className="nxw-progress" aria-label={`${current.label} progress`}>
        {current.stages.map((stage, index) => {
          const completed = nodeCount > 0 && (!isGenerating || index === 0);
          const active = isGenerating ? index === Math.min(1, current.stages.length - 1) : nodeCount > 0 && index === 2;
          return <React.Fragment key={stage}><span className={`${completed ? 'is-done' : ''} ${active ? 'is-active' : ''}`}><i />{stage}</span>{index < current.stages.length - 1 && <b />}</React.Fragment>;
        })}
      </div>
    </section>
  );
}

export function NextModeEmptyState({ mode, onExampleClick, hasMemory }) {
  const current = getNextMode(mode);
  return (
    <div className="nxw-empty" style={{ '--nxw-accent': current.color }}>
      <div className="nxw-empty-grid" />
      <span className="nxw-empty-kicker">YOUR OUTPUT · {current.deliverable.toUpperCase()}</span>
      <div className="nxw-empty-icon">{current.icon}</div>
      <h2>{current.job}</h2>
      <p>{current.description}</p>
      {hasMemory && <div className="nxw-memory-note"><span>◇</span><div><b>Claude memory is in the room</b><small>Relevant project history and prior decisions will be included automatically.</small></div></div>}
      <div className="nxw-examples">
        <small>START WITH AN EXAMPLE</small>
        {current.examples.map(example => <button key={example} onClick={() => onExampleClick(example)}>{example}<span>↗</span></button>)}
      </div>
      <p className="nxw-empty-hint">Enter your request below. You can attach files, URLs, email, or local project context.</p>
    </div>
  );
}

